import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useProgress } from "@react-three/drei";
import { Gift, Check, ArrowRight } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion.js";
import handleOpenApp from "../Utility/HandleOpenApp";
import WithStar from "./WithStar.jsx";
import { CarStage, Scene } from "./three/index.js";
import { heroPromises } from "./data.js";

import styles from "./Hero.module.css";

/* ─────────────────────────────────────────────────────────────
   Hero — cinematic, scroll-driven reveal of a real 3D car (React
   Three Fiber + a real .glb model), replacing the earlier flat
   hand-drawn SVG car + static two-column layout.

   The section is a tall (300svh) scroll track; the stage pins for
   the ride and the car turns a full 180° (front → side → rear) as
   you scroll through it, while three short text "beats" dissolve
   in and out at different corners of the screen — nothing is ever
   laid out as a conventional split. See `three/CarStage.jsx` /
   `three/carStageConfig.js` for how the camera guarantees the car
   is never cropped through the turn.

   Scroll wiring is the one deliberate departure from the reference
   this was ported from: that version drove `progress` off framer-
   motion's own `useScroll`, with the pinned stage done in plain CSS
   `position: sticky`. This page already runs GSAP ScrollSmoother on
   "/" (see App.jsx) — sticky positioning and Smoother don't reliably
   agree, since Smoother works by transforming a content wrapper, not
   by moving the real scrollbar. So the pin here is done with GSAP's
   own ScrollTrigger (`pin: stageRef`), which auto-detects Smoother's
   transformed wrapper and pins via transform instead of
   `position: fixed` — same engine the rest of the page already
   trusts for its scroll effects — and its `onUpdate` just writes the
   0–1 progress into a motion value, so everything downstream (the
   spring, the beat opacity/y transforms, the 3D camera/car) is
   unchanged from a plain framer `useScroll` version.

   Reduced motion skips the pinned/scroll-jacking mechanism entirely
   rather than just disabling easing on top of it — a 300vh scroll
   track that does nothing is worse than not having one — and falls
   back to a single static three-quarter composition with the same
   visual language.
───────────────────────────────────────────────────────────── */
export default function Hero() {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (prefersReducedMotion) {
    return <StaticHero />;
  }

  return <CinematicHero />;
}

function CinematicHero() {
  const trackRef = useRef(null);
  const stageRef = useRef(null);
  const { active: modelLoading, progress: modelProgress } = useProgress();

  const rawProgress = useMotionValue(0);
  const progress = useSpring(rawProgress, { stiffness: 55, damping: 20, mass: 0.4 });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const track = trackRef.current;
    const stage = stageRef.current;
    if (!track || !stage) return undefined;

    const trigger = ScrollTrigger.create({
      trigger: track,
      start: "top top",
      end: "bottom bottom",
      scrub: true,
      pin: stage,
      anticipatePin: 1,
      onUpdate: (self) => rawProgress.set(self.progress),
    });

    return () => trigger.kill();
  }, [rawProgress]);

  const degreeLabel = useTransform(progress, (p) => `${Math.round(p * 180)}°`.padStart(4, "0"));
  const railScale = useTransform(progress, [0, 1], [0, 1]);

  // Beat windows are deliberately non-overlapping: each beat fully
  // reaches opacity 0 (with an explicit gap below) before the next
  // starts fading in. beat1 -> beat2Left and beat2Right -> beat3
  // intentionally reuse the same on-screen corner, which only reads
  // as a clean cut/cross-dissolve if the two are never simultaneously
  // rendered with nonzero opacity — the old windows overlapped by
  // 2-3% of scroll progress, which stacked, e.g., the checklist and
  // the CTA button on top of each other mid-scroll.
  const beat1Opacity = useTransform(progress, [0, 0.23, 0.28], [1, 1, 0]);
  const beat1Y = useTransform(progress, [0, 0.23, 0.28], [0, 0, -14]);

  const beat2Opacity = useTransform(progress, [0.33, 0.4, 0.56, 0.62], [0, 1, 1, 0]);
  const beat2YLeft = useTransform(progress, [0.33, 0.4, 0.56, 0.62], [22, 0, 0, -14]);
  const beat2YRight = useTransform(progress, [0.33, 0.4, 0.56, 0.62], [-22, 0, 0, 14]);

  const beat3Opacity = useTransform(progress, [0.67, 0.75, 1], [0, 1, 1]);
  const beat3Y = useTransform(progress, [0.67, 0.75, 1], [22, 0, 0]);

  const [headlinePromise, ...restPromises] = heroPromises;

  return (
    <section id="hero" className={styles.hero}>
      <div ref={trackRef} className={styles.track}>
        <div ref={stageRef} className={styles.stage}>
          <div className={styles.atmosphere} aria-hidden="true">
            <span className={styles.glowA} />
            <span className={styles.glowB} />
            <span className={styles.glowC} />
            <span className={styles.grain} />
            <span className={styles.vignette} />
          </div>

          <div className={styles.canvasLayer}>
            <Scene className={styles.canvas}>
              <CarStage progress={progress} />
            </Scene>

            {modelLoading && (
              <div className={styles.loading} role="status" aria-live="polite">
                <span className={styles.loadingBar}>
                  <span className={styles.loadingFill} style={{ width: `${Math.round(modelProgress)}%` }} />
                </span>
                <span className={styles.loadingLabel}>Loading car model…</span>
              </div>
            )}
          </div>

          <div className={styles.chrome}>
            <span className={styles.chromeIndex}>N° 01</span>
            <motion.span className={styles.chromeDegree}>{degreeLabel}</motion.span>
            <div className={styles.chromeRailTrack}>
              <motion.span className={styles.chromeRailFill} style={{ scaleY: railScale }} />
            </div>
            <div className={styles.scrollHint}>
              <span className={styles.scrollHintDot} />
              Scroll to turn the car
            </div>
          </div>

          <motion.div className={styles.beat1} style={{ opacity: beat1Opacity, y: beat1Y }}>
            <span className={styles.badge}>
              <Gift className={styles.badgeIcon} strokeWidth={2} />
              <WithStar text={headlinePromise} />
            </span>
            <h1 className={styles.heading}>Plan Your Car</h1>
            <p className={styles.subheading}>From Home to Quick Delivery at Home</p>
          </motion.div>

          <motion.div className={styles.beat2Left} style={{ opacity: beat2Opacity, y: beat2YLeft }}>
            <p className={styles.supporting}>You Don&apos;t Have to Go Anywhere.</p>
          </motion.div>

          <motion.div className={styles.beat2Right} style={{ opacity: beat2Opacity, y: beat2YRight }}>
            <ul className={styles.checklist}>
              {restPromises.map((item) => (
                <li key={item}>
                  <span className={styles.checkIcon}>
                    <Check width={16} height={16} strokeWidth={2.6} />
                  </span>
                  <WithStar text={item} />
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className={styles.beat3} style={{ opacity: beat3Opacity, y: beat3Y }}>
            <button onClick={() => handleOpenApp()} className={styles.cta}>
              Create My Car Plan
              <ArrowRight width={18} height={18} />
            </button>
            <p className={styles.microcopy}>No cost to start · Your money stays in your control</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * Reduced-motion fallback: the same visual language (atmosphere, huge
 * type, the car) collapsed into one static, non-scroll-jacked composition.
 * The car holds a fixed flattering angle (see `CarModel`'s reduced-motion
 * branch) instead of turning.
 */
function StaticHero() {
  const staticProgress = useMotionValue(0.4);
  const [headlinePromise, ...restPromises] = heroPromises;

  return (
    <section id="hero" className={styles.hero}>
      <div className={styles.staticStage}>
        <div className={styles.atmosphere} aria-hidden="true">
          <span className={styles.glowA} />
          <span className={styles.glowB} />
          <span className={styles.glowC} />
          <span className={styles.grain} />
          <span className={styles.vignette} />
        </div>

        <div className={styles.canvasLayer}>
          <Scene className={styles.canvas}>
            <CarStage progress={staticProgress} />
          </Scene>
        </div>

        <div className={styles.chrome}>
          <span className={styles.chromeIndex}>N° 01</span>
        </div>

        <div className={styles.staticHeadline}>
          <span className={styles.badge}>
            <Gift className={styles.badgeIcon} strokeWidth={2} />
            <WithStar text={headlinePromise} />
          </span>
          <h1 className={styles.heading}>Plan Your Car</h1>
          <p className={styles.subheading}>From Home to Quick Delivery at Home</p>
        </div>

        <div className={styles.staticLower}>
          <p className={styles.supporting}>You Don&apos;t Have to Go Anywhere.</p>
          <ul className={styles.checklist}>
            {restPromises.map((item) => (
              <li key={item}>
                <span className={styles.checkIcon}>
                  <Check width={16} height={16} strokeWidth={2.6} />
                </span>
                <WithStar text={item} />
              </li>
            ))}
          </ul>
          <button onClick={() => handleOpenApp()} className={styles.cta}>
            Create My Car Plan
            <ArrowRight width={18} height={18} />
          </button>
          <p className={styles.microcopy}>No cost to start · Your money stays in your control</p>
        </div>
      </div>
    </section>
  );
}
